import { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Collapse from '@mui/material/Collapse';
import { faCaretDown } from '@rtkwlf/pro-solid-svg-icons/faCaretDown';
import { faCaretUp } from '@rtkwlf/pro-solid-svg-icons/faCaretUp';
import { matchPath, useLocation } from 'react-router-dom';

import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '../../../components/list';
import { Tooltip } from '../../../components/tooltip';
import { styled } from '../../../styles';

import type { ExternalLink, InternalLink } from './menu-link-types';
import { useNavigation } from './navigation-provider';
import { RouteMenu } from './route-menu';

type CollapsableMenuChildItem =
  | Omit<ExternalLink, 'icon'>
  | Omit<InternalLink, 'icon'>;

export interface CollapsableMenuItem {
  title: string;
  icon: JSX.Element;
  children: CollapsableMenuChildItem[];
}

const StyledListItem = styled(ListItem)<{
  open?: boolean;
}>(({ theme, open }) => ({
  backgroundColor: open ? theme.palette.action.hover : 'transparent',
}));

type CollapsableMenuProps = {
  item: CollapsableMenuItem;
};

const isMenuLinkInternal = (
  item: CollapsableMenuChildItem,
): item is InternalLink => 'segment' in item;

export const isChildRouteSelected = (
  segment: string,
  pathname: string,
  prefix: string,
  // Should be `true` if this pattern should match the entire URL pathname.
  matchSegmentOnly = false,
) => {
  return !!matchPath(
    { path: `${prefix}${segment}`, end: matchSegmentOnly },
    pathname,
  );
};

/** @experimental In active development. Avoid using until further notice. */
export const CollapsibleMenu = ({
  item: { icon, title, children },
}: CollapsableMenuProps) => {
  const location = useLocation();
  const { routePrefix, isNavExpanded, handleNavMenuClick } = useNavigation();
  const isAnyChildSelected = useMemo(
    () =>
      children.some(
        (childItem) =>
          isMenuLinkInternal(childItem) &&
          isChildRouteSelected(
            childItem.segment,
            location.pathname,
            routePrefix,
            childItem.matchSegmentOnly,
          ),
      ),
    [children, location.pathname, routePrefix],
  );

  const [open, setOpen] = useState(isAnyChildSelected);

  const listItem = (
    <StyledListItem disablePadding open={isAnyChildSelected}>
      <ListItemButton onClick={() => handleNavMenuClick(open, setOpen)}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={title} />
        <FontAwesomeIcon icon={open ? faCaretUp : faCaretDown} />
      </ListItemButton>
    </StyledListItem>
  );

  return (
    <>
      {!isNavExpanded ? (
        <Tooltip title={title} placement="right">
          {listItem}
        </Tooltip>
      ) : (
        listItem
      )}
      <Collapse in={open && isNavExpanded} timeout="auto" unmountOnExit>
        <List disablePadding dense>
          {children.map((item) => (
            <RouteMenu key={`${title}-${item.title}`} item={item} nested />
          ))}
        </List>
      </Collapse>
    </>
  );
};
